(ns practitest.frontend.components2.dropdown
  (:require [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.common.utils :refer [example development-only register-components!]]
            [reagent.core :as reagent]
            [goog.object :as gobj]))

(defn ^{:component? true} dropdown-menu-item
  "Dropdown menu item. Can be used standalone, but expected to be a child of `dropdown-menu-body`

  * `text` - text to show on menu item
  * `disabled?` - whether that item is a disabled
  * `id` - HTML id
  * `icon` - icon to show alongside the item menu
  * `mixpanel` - mixpanel event and attributes map (in a vector)
  * `href` - href for anchor elements
  * `new-window?` - open in a new window
  * `on-click` - handler for clicking on menu item
  * `tooltip` - optional tooltip to show for this menu item"
  [{:keys [text
           disabled?
           icon
           id

           mixpanel
           href
           new-window?

           tooltip

           on-click
           custom-body]
    :as props}]
  [tooltip/mux-tooltip
   {}
   tooltip
   (fn [tooltip-props]
     [:a.m-dropdown-menu-item
      (cond-> {:class    (dom/classes-pairs-v :disabled disabled?)
               :on-click (fn [e]
                           (when-not disabled?
                             (when mixpanel
                               (apply mixpanel/track mixpanel))
                             (when on-click
                               (on-click e))))
               :href     (if disabled? nil href)}

              (some? tooltip)
              (merge tooltip-props)

              id
              (dom/add-test-id-and-id id)

              new-window?
              (assoc :target "_blank"))

      (when icon
        [:span.icon-holder
         icon])

      text

      custom-body])])

(defn ^{:component true} dropdown-menu-body
  "Menu body wrapper, will filter nils from `items` and apply correct CSS class.

  * `props` will be propagated to top-level div
  * `item` - can be anything, usual pattern is to use `dropdown-menu-item` to render menu options"
  [props & items]
  (into [:div.m-dropdown-menu-body props]
        (remove nil?)
        items))

(defn ^{:component true} dropdown-menu
  "Dropdown menu, any trigger component will work

  * `toggle-element-fn` - will be passed object with `hide!` and `toggle!` functions,
         should return reagent-consumable thing that will trigger menu display somehow
  * `overlay-props` see `practitest.frontend.components.overlay2/floating-ui-overlay`
  * `menu-body` is supposed to be a `dropdown-menu-body` but can be anything - will be rendered in an overlay"
  [toggle-element-fn overlay-props menu-body]
  (reagent/with-let [local-state (reagent/atom {:show? false})
                     hide! (fn []
                             (swap! local-state assoc :show? false))
                     toggle! (fn []
                               (swap! local-state update :show? not))]
    (let [show? (:show? @local-state)]
      [overlay2/floating-ui-overlay
       (merge {:on-request-close hide!
               :show? show?
               :offset 5
               :placement "bottom-start"}
              overlay-props)
       (toggle-element-fn {:toggle! toggle!})
       menu-body])))

(defn- get-jsx-children [props]
  ; Used to handle both single and multiple children(props) and return them as a collection.
  (if-let [children (gobj/get props "children")]
    (if (coll? children)
      children
      [children])
    []))

;; React friendly wrappers
(defn DropdownMenu
  [props]
  (let [element-fn (gobj/get props "toggleElement")]
    (reagent/as-element (into [dropdown-menu
                               (fn [{:keys [toggle!]}]
                                 ;; rename toggle! -> toggle so it's easier on TS side
                                 (element-fn #js {:toggle toggle!}))
                               (js->clj (gobj/get props "overlayProps") :keywordize-keys true)]
                              (get-jsx-children props)))))

(defn DropdownMenuBody
  [props]
  (reagent/as-element (into [dropdown-menu-body {}]
                            (get-jsx-children props))))

(defn DropdownMenuItem
  [props]
  (reagent/as-element [dropdown-menu-item
                       {:text (gobj/get props "text")
                        :icon (gobj/get props "icon")
                        :href (gobj/get props "href")
                        :disabled? (gobj/get props "disabled")
                        :on-click (gobj/get props "onClick")
                        :custom-body (seq (get-jsx-children props))}]))

(defn ^{:component? true} dropdown-button
  "Dropdown button component

  * `button props` see `practitest.frontend.components2.buttons/button`
  * `overlay-props` see `practitest.frontend.components.overlay2/floating-ui-overlay`
  * items can be anything, but expected to be a list of `dropdown-item` components"
  [button-props overlay-props & items]
  (let [on-click (:on-click button-props)
        text (:text button-props)
        primary? (:primary? button-props)]
    [dropdown-menu
     (fn [{:keys [toggle!]}]
       [buttons/button (assoc button-props
                         :text text
                         :end-icon (if primary?
                                     [icons/svg-chevron-down]
                                     [icons/mux-chevron-down2])
                         :on-click (fn [e]
                                     (toggle!)
                                     (when on-click
                                       (on-click e))))])
     overlay-props
     (into [dropdown-menu-body {}]
           items)]))

(development-only
  (defn ^{:component-example dropdown-menu-item} dropdown-item-examples []
    [:div
     {:style {:max-width "200px"
              :border "1px solid var(--pt-color-border-light)"}}
     [dropdown-menu-item {:text "Item with text"}]
     [dropdown-menu-item {:text "Item with text and icon"
                          :icon [icons/mux-pencil-rename]}]])

  (defn ^{:component-example dropdown-button} dropdown-example1 []
    [:div
     [dropdown-button
      {:text "Dropdown Button"}
      {}
      [dropdown-menu-item {:text "Hey !"}]
      [dropdown-menu-item {:disabled? true
                           :text "Disabled text"}]
      [dropdown-menu-item {:disabled? true
                           :text "Disabled text with an icon"
                           :icon [icons/mux-milestones]}]
      [dropdown-menu-item {:text "Disabled text with an icon swith some really long text here"
                           :icon [icons/mux-milestones]}]
      [dropdown-menu-item {:text "Hey with an icon and tooltip !"
                           :tooltip "I'm a tooltip !"
                           :icon [icons/mux-pencil]}]]]))

(register-components!)
